import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Clock, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { COVERAGE } from "@/data/coverage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#101114] border border-white/10 focus:border-red-600/60 focus:outline-none rounded-md px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 transition-colors duration-300";

export default function Contact() {
  const { lang, t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service_interest: "",
    budget_range: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.successTitle, { description: t.contact.successDesc });
      setForm({ name: "", email: "", company: "", service_interest: "", budget_range: "", message: "" });
    } catch {
      toast.error(t.contact.errorTitle, { description: t.contact.errorDesc });
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="relative px-6 lg:px-12 pt-20 md:pt-28 pb-28 overflow-hidden" data-testid="contact-page">
      <Seo title={t.seo.contactTitle} description={t.contact.desc} />
      <div className="absolute -left-40 top-0 w-[480px] h-[480px] spotlight-red" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto">
        <Reveal y={12}>
          <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-red-500 mb-6">{t.contact.kicker}</p>
          <h1 className="f-display font-black tracking-tight leading-[0.95] text-[12vw] sm:text-6xl lg:text-8xl" data-testid="contact-title">
            {t.contact.titleA}
            <span className="f-serif italic font-normal text-red-500 block">{t.contact.titleB}</span>
          </h1>
          <p className="mt-8 text-slate-400 text-base sm:text-lg max-w-xl leading-relaxed">{t.contact.desc}</p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <Reveal delay={0.1} className="lg:col-span-7">
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="contact-form">
              <div>
                <label htmlFor="cf-name" className="f-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-2">
                  {t.contact.name} *
                </label>
                <input id="cf-name" data-testid="contact-form-name" required value={form.name} onChange={set("name")} placeholder={t.contact.namePh} className={inputCls} />
              </div>
              <div>
                <label htmlFor="cf-email" className="f-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-2">
                  {t.contact.email} *
                </label>
                <input id="cf-email" data-testid="contact-form-email" type="email" required value={form.email} onChange={set("email")} placeholder={t.contact.emailPh} className={inputCls} />
              </div>
              <div>
                <label htmlFor="cf-company" className="f-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-2">
                  {t.contact.company}
                </label>
                <input id="cf-company" data-testid="contact-form-company" value={form.company} onChange={set("company")} placeholder={t.contact.companyPh} className={inputCls} />
              </div>
              <div>
                <label htmlFor="cf-service" className="f-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-2">
                  {t.contact.service}
                </label>
                <select id="cf-service" data-testid="contact-form-service" value={form.service_interest} onChange={set("service_interest")} className={inputCls}>
                  <option value="">—</option>
                  {t.contact.serviceOptions.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-budget" className="f-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-2">
                  {t.contact.budget}
                </label>
                <select id="cf-budget" data-testid="contact-form-budget" value={form.budget_range} onChange={set("budget_range")} className={inputCls}>
                  <option value="">—</option>
                  {t.contact.budgetOptions.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-message" className="f-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-2">
                  {t.contact.message} *
                </label>
                <textarea id="cf-message" data-testid="contact-form-message" required rows={6} value={form.message} onChange={set("message")} placeholder={t.contact.messagePh} className={`${inputCls} resize-none`} />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-form-submit"
                  className="group inline-flex items-center gap-3 bg-red-600 hover:bg-red-500 disabled:opacity-60 transition-colors duration-300 text-white f-mono text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-full"
                >
                  {sending ? t.contact.sending : t.contact.submit}
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </form>
          </Reveal>

          <Reveal delay={0.2} className="lg:col-span-5">
            <div className="bg-[#101114] border border-white/10 rounded-lg p-8 md:p-10" data-testid="contact-info-card">
              <h2 className="f-display text-2xl font-bold tracking-tight">{t.contact.infoTitle}</h2>
              <ul className="mt-8 space-y-6 text-sm">
                <li className="flex items-start gap-4">
                  <MapPin size={18} className="text-red-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-slate-200" data-testid="contact-address">{t.contact.address}</p>
                    <p className="text-slate-500 mt-1">{t.contact.scope}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Mail size={18} className="text-red-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="f-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.contact.emailLabel}</p>
                    <a href="mailto:info@rvfilm.it" data-testid="contact-email-link" className="text-slate-200 hover:text-red-500 transition-colors">
                      info@rvfilm.it
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Clock size={18} className="text-red-500 mt-0.5 shrink-0" />
                  <p className="text-slate-200" data-testid="contact-reply-time">{t.contact.reply}</p>
                </li>
              </ul>
            </div>
            <div className="mt-6 rounded-lg overflow-hidden border border-white/10" data-testid="contact-map">
              <iframe
                title="RV FILM — Via U. Braschi 8, Santarcangelo di Romagna"
                src="https://maps.google.com/maps?q=Via%20U.%20Braschi%208%2C%2047822%20Santarcangelo%20di%20Romagna%20RN%2C%20Italia&z=15&output=embed"
                className="w-full h-64 grayscale invert-[0.9] contrast-[0.9]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
        <div className="mt-20 border-t border-white/10 pt-16" data-testid="coverage-section">
          <Reveal>
            <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-red-500 mb-6">{COVERAGE[lang].kicker}</p>
            <h2 className="f-display text-3xl sm:text-4xl font-bold tracking-tight">{COVERAGE[lang].title}</h2>
            <p className="mt-5 text-slate-400 text-sm md:text-base leading-relaxed max-w-2xl">{COVERAGE[lang].text}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-10 f-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">{COVERAGE[lang].regionsLabel}</p>
            <div className="flex flex-wrap gap-2" data-testid="coverage-regions">
              {COVERAGE[lang].regions.map((r) => (
                <span key={r} className="f-mono text-[10px] uppercase tracking-[0.12em] bg-white/5 border border-white/10 rounded-full px-3.5 py-1.5 text-slate-300">
                  {r}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 f-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">{COVERAGE[lang].citiesLabel}</p>
            <p className="text-sm text-slate-400 leading-loose max-w-4xl" data-testid="coverage-cities">
              {COVERAGE[lang].cities.join(" · ")}
            </p>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
